/**
 * Access requests (ADR-034): members who asked for a role. A request grants
 * nothing; an access manager answers it by giving a role (which closes it) or
 * declining it (the person is told in Fias).
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import type { AppAccessScreenDto, VaultHolderMemberDto } from '@/lib/api';
import { useCloseAppAccessRequest, type AppAccessScope } from '../../hooks/useAppAccess';
import { describeAppAccessError, formatDate, memberLabel, roleLabel } from './shared';

interface RequestsSectionProps {
  scope: AppAccessScope;
  screen: AppAccessScreenDto;
  members: readonly VaultHolderMemberDto[];
  onGrant: (request: { principalId: string; roleId: string | null }) => void;
}

export function RequestsSection({ scope, screen, members, onGrant }: RequestsSectionProps) {
  const decline = useCloseAppAccessRequest(scope);
  const byPrincipal = new Map(members.map((m) => [m.principalId, m]));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Access requests</CardTitle>
        <CardDescription>
          Asking grants nothing. Give a role to answer, or decline — the person is told either way.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {screen.requests.length === 0 && (
          <p className="text-muted-foreground">No one is waiting for access.</p>
        )}
        {screen.requests.map((r) => (
          <div
            key={r.requestId}
            className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border p-2"
          >
            <div className="min-w-0">
              <p>
                <span className="font-medium">
                  {memberLabel(byPrincipal.get(r.principalId), r.principalId)}
                </span>{' '}
                asked for{' '}
                {r.roleId ? (
                  <span className="font-medium">{roleLabel(screen.access.roles, r.roleId)}</span>
                ) : (
                  'access'
                )}{' '}
                <span className="text-muted-foreground">on {formatDate(r.createdAt)}</span>
              </p>
              {r.message && <p className="break-words text-muted-foreground">“{r.message}”</p>}
            </div>
            {screen.seat.manager && (
              <span className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => onGrant({ principalId: r.principalId, roleId: r.roleId })}
                >
                  Give a role…
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={decline.isPending}
                  onClick={() =>
                    decline.mutate(r.requestId, {
                      onError: (err) =>
                        toast({
                          title: 'Not declined',
                          description: describeAppAccessError(err),
                          variant: 'destructive',
                        }),
                    })
                  }
                >
                  Decline
                </Button>
              </span>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
